import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
	View,
	ScrollView,
	TouchableOpacity,
	Dimensions,
	Platform,
	Animated,
	Image,
} from 'react-native'
import Colors from '../../../constants/Colors'
import {
	mediumTextSize,
	smallTextSize,
	sTextSize,
	xsTextSize,
	SCREEN_MAX_WIDTH,
} from '../../../constants/GlobalStyle'
import { RestaurantList } from './RestaurantList'
import { checkSnooze } from '../../../helpers/checkSnooze'
import { ORGANIZATION_IMAGES_MAP } from '../../../assets/images/cuisines/content'
import CopyrightFooter from '../../../components/Footers/CopyrightFooter'
import { MaterialIcons } from '@expo/vector-icons'
import { Text } from '../../../components/Themed'
import { useAppSelector } from '../../../state/hooks'
import ClubKitchenFooter from '../../../components/Footers/ClubKitchenFooter'
import { isRestaurantOpen } from '../../../helpers/restaurantHours'
import { styles } from '../style'
import { colors, Stack } from '../../../components/DesignSystem'
import { useOrganizationStyles } from '../../../hooks/useOrganizationStyles'
export default function CuisineSelector({ navigation }) {
	const CONTENT_MARGIN = 20
	const cuisineImages = ORGANIZATION_IMAGES_MAP[global.org]

	const [selectedCuisine, setSelectedCuisine] = useState('Show All')
	const [scrollX, setScrollX] = useState(0)
	const [isAtEnd, setIsAtEnd] = useState(false)
	const [contentWidth, setContentWidth] = useState(0)
	const scrollViewRef = useRef(null)
	const [filteredRestaurants, setFilteredRestaurants] = useState([])
	const windowWidth = Dimensions.get('window').width

	const { loadedData, restaurants } = useAppSelector(state => state.data)
	const { isTestMode } = useAppSelector(state => state.persisted)
	const {
		isSandbox,
		webOrdersEnabled,
		allowOrdersOutsideOrgHours,
		localTimezone,
		appDisplayOptions,
	} = useAppSelector(state => state.settings)
	const { showCuisines, showCategories, showBrands } = appDisplayOptions

	const { appBackground, buttonAlternateColor } = useOrganizationStyles()

	const SHOP_BY_RESTAURANT_EXTRA_HEIGHT = showCategories || showBrands ? 60 : 0

	const cuisinesDisabled = useMemo(
		() => !showCuisines || (Platform.OS === 'web' && !webOrdersEnabled && !isTestMode),
		[showCuisines, webOrdersEnabled, isTestMode]
	)

	const availableCuisines = useMemo(() => {
		const loadedDataCuisine =
			loadedData.orgCuisines.length > 0 ? loadedData.orgCuisines : loadedData.cuisines
		const sortedCuisines = [...loadedDataCuisine].sort((a, b) => {
			if (a.ordinal === undefined) return 1
			if (b.ordinal === undefined) return -1
			return a.ordinal - b.ordinal
		})

		const filtered =
			restaurants.length > 0
				? sortedCuisines.filter(cuisine =>
						restaurants.some(restaurant => restaurant.cuisines.includes(cuisine.id.toLowerCase()))
					)
				: []

		if (restaurants.length > 0) {
			filtered.unshift({
				title: 'Show All',
				ordinal: 0,
				id: 'Show All',
			})
		}

		return filtered
	}, [loadedData.cuisines, restaurants])

	const isRestaurantOpenFn = useCallback(
		restaurant =>
			isRestaurantOpen(
				restaurant,
				loadedData[global.org].hours ?? null,
				allowOrdersOutsideOrgHours,
				localTimezone
			),
		[loadedData[global.org].hours, allowOrdersOutsideOrgHours, localTimezone]
	)

	useEffect(() => {
		// Used to filter restaurants if valid and cuisine is included. Can refactor cuisine filter to its own method.
		const byValidAndCuisineIncluded = restaurant => {
			const isNotSnoozed = !checkSnooze(restaurant.isSnoozed, restaurant.snoozeUntil)
			const isEnabled = restaurant.isEnabled
			// console.log(restaurant)

			// console.log(`${restaurant.id} is ${restaurant.isEnabled}`)
			const isValidTestRestaurant = !restaurant.isTest || isSandbox || isTestMode
			const cuisineIncluded = selectedCuisine
				? restaurant.cuisines.includes(selectedCuisine.toLowerCase())
				: true

			if (!isEnabled) {
				return false
			}
			return (
				selectedCuisine === 'Show All' || (cuisineIncluded && isNotSnoozed && isValidTestRestaurant)
			)
		}

		// Used to sort restaurants from open to closed in their original order.
		const fromOpenToClosed = (restaurantA, restaurantB) => {
			const aIsOpen = isRestaurantOpenFn(restaurantA)
			const bIsOpen = isRestaurantOpenFn(restaurantB)
			if (aIsOpen === bIsOpen) {
				return 0
			}
			return aIsOpen ? -1 : 1
		}

		const updatedFilteredRestaurants =
			restaurants.length > 0
				? restaurants.filter(byValidAndCuisineIncluded).sort(fromOpenToClosed)
				: []

		setFilteredRestaurants(updatedFilteredRestaurants)
	}, [isTestMode, selectedCuisine, restaurants, isSandbox])

	const handleCuisinePress = useCallback(cuisine => {
		setSelectedCuisine(prev => (prev === cuisine ? 'Show All' : cuisine))
	}, [])

	const handleScroll = useCallback(event => {
		const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent
		setIsAtEnd(contentOffset.x + layoutMeasurement.width >= contentSize.width)
		setScrollX(contentOffset.x)
	}, [])

	const scrollRight = useCallback(() => {
		scrollViewRef.current?.scrollTo({ x: scrollX + 200, animated: true })
	}, [scrollX])

	const scrollLeft = useCallback(() => {
		scrollViewRef.current?.scrollTo({ x: scrollX - 200, animated: true })
	}, [scrollX])

	const scrollViewHeight = 160
	const buttonSize = 40
	const isAtStart = scrollX <= 0

	return (
		<>
			<View
				style={{
					flex: 1,
					marginTop: Platform.OS === 'web' ? 0 : -40,
					maxWidth: SCREEN_MAX_WIDTH,
					alignSelf: 'center',
					width: '100%',
					marginBottom: 20,
					display: Platform.OS === 'web' && !webOrdersEnabled && !isTestMode ? 'none' : 'flex',
				}}
			>
				<Text
					style={[
						styles.subHeaderText,
						{
							display: showCategories || showBrands ? 'flex' : 'none',
							marginTop: Platform.OS === 'web' ? 40 : -20,
							paddingVertical: 0,
							marginBottom: showCuisines && global.kiosk ? 15 : showCuisines ? 0 : 30,
							marginHorizontal: '4.5%',
						},
					]}
				>
					{'SHOP BY RESTAURANT'}
				</Text>
				{Platform.OS === 'web' && !isAtStart && (
					<TouchableOpacity
						onPress={scrollLeft}
						style={{
							display:
								availableCuisines.length > 0 &&
								!cuisinesDisabled &&
								contentWidth + CONTENT_MARGIN >= windowWidth
									? 'flex'
									: 'none',
							position: 'absolute',
							left: Dimensions.get('window').width <= 1225 ? 0 : buttonSize,
							top: scrollViewHeight / 2 - buttonSize / 2 + 10 + SHOP_BY_RESTAURANT_EXTRA_HEIGHT,
							zIndex: 2,
							backgroundColor: 'white',
							width: buttonSize,
							height: buttonSize,
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: buttonSize / 2,
							shadowColor: '#000',
							shadowOffset: { width: 0, height: 2 },
							shadowOpacity: 0.25,
							shadowRadius: 3.84,
							elevation: 5,
						}}
					>
						<MaterialIcons name="chevron-left" size={24} color="black" />
					</TouchableOpacity>
				)}

				<ScrollView
					ref={scrollViewRef}
					horizontal={true}
					showsHorizontalScrollIndicator={false}
					contentContainerStyle={
						global.kiosk && {
							display: availableCuisines.length > 0 && !cuisinesDisabled ? 'flex' : 'none',
							paddingVertical: 0,
							maxHeight: scrollViewHeight,
							paddingHorizontal: Dimensions.get('window').width <= 1225 ? '4%' : 0,
							marginLeft: Dimensions.get('window').width <= 1225 ? 0 : 10,
							alignSelf: 'center',
						}
					}
					style={
						!global.kiosk && {
							display: availableCuisines.length > 0 && !cuisinesDisabled ? 'flex' : 'none',
							paddingVertical: 20,
							maxHeight: scrollViewHeight,
							paddingHorizontal: Dimensions.get('window').width <= 1225 ? '4%' : 0,
							marginLeft: Dimensions.get('window').width <= 1225 ? 0 : 10,
							maxWidth: 1215,
							alignSelf: 'center',
							width: contentWidth + CONTENT_MARGIN >= windowWidth ? '100%' : null,
						}
					}
					onScroll={handleScroll}
					onContentSizeChange={(w, h) => setContentWidth(w)}
					scrollEventThrottle={16}
				>
					{availableCuisines.map((cuisine, index) => {
						const cuisineName = cuisine.title
						const isSelected = selectedCuisine === cuisineName

						const rotateAnim = useRef(new Animated.Value(0)).current

						const rotateStyle = {
							transform: [
								{ perspective: 1000 },
								{
									rotate: rotateAnim.interpolate({
										inputRange: [0, 1],
										outputRange: ['0deg', '15deg'],
									}),
								},
							],
						}

						useEffect(() => {
							Animated.spring(rotateAnim, {
								toValue: isSelected ? 1 : 0,
								friction: 50,
								tension: 75,
								useNativeDriver: true,
							}).start()
						}, [isSelected])

						return (
							<TouchableOpacity
								key={cuisine.id}
								onPress={() => handleCuisinePress(cuisineName)}
								style={{
									paddingVertical: 5,
									paddingHorizontal: 12,
									alignItems: 'center',
								}}
							>
								<Stack gap="md">
									<View
										style={{
											width: 80,
											height: 80,
											borderRadius: 40,
											backgroundColor: !isSelected ? appBackground : colors.grayTranslucent,
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<Animated.View
											style={[
												{
													width: 80,
													height: 80,
													backgroundColor: !isSelected ? appBackground : colors.grayTranslucent,
													borderRadius: 40,
													justifyContent: 'center',
													alignItems: 'center',
												},
												rotateStyle,
											]}
										>
											<Image
												source={
													cuisine?.imageUrl ? { uri: cuisine.imageUrl } : cuisineImages[cuisine.id]
												}
												style={{
													width: 50,
													height: 50,
												}}
											/>
										</Animated.View>
									</View>
									<Text
										style={{
											width: 80,
											maxHeight: 40,
											fontSize: Dimensions.get('window').width < 1200 ? 14 : sTextSize,
											fontWeight: '500',
											flexWrap: 'wrap',
											textAlign: 'center',
											letterSpacing: -0.5,
										}}
									>
										{cuisineName}
									</Text>
								</Stack>
							</TouchableOpacity>
						)
					})}
				</ScrollView>
				{Platform.OS === 'web' && !isAtEnd && (
					<TouchableOpacity
						onPress={scrollRight}
						style={{
							display:
								contentWidth + CONTENT_MARGIN >= windowWidth && !cuisinesDisabled ? 'flex' : 'none',
							position: 'absolute',
							right: Dimensions.get('window').width <= 1225 ? 0 : buttonSize - 10,
							top: scrollViewHeight / 2 - buttonSize / 2 + 10 + SHOP_BY_RESTAURANT_EXTRA_HEIGHT,
							zIndex: 2,
							backgroundColor: 'white',
							width: buttonSize,
							height: buttonSize,
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: buttonSize / 2,
							shadowColor: '#000',
							shadowOffset: { width: 0, height: 2 },
							shadowOpacity: 0.25,
							shadowRadius: 3.84,
							elevation: 5,
						}}
					>
						<MaterialIcons name="chevron-right" size={24} color="black" />
					</TouchableOpacity>
				)}

				{/* Restaurant List */}
				<RestaurantList navigation={navigation} visibleRestaurants={filteredRestaurants} />
			</View>
			{Platform.OS === 'web' && !(showCategories || showBrands) && (
				<>
					{global.org === 'clubkitchen' && <ClubKitchenFooter />}
					<CopyrightFooter showIcons={availableCuisines.length > 0 && !cuisinesDisabled} />
				</>
			)}
		</>
	)
}
